import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionEditor from '@/components/SectionEditor';
import RobotHandBox from '@/components/AnimatedCartonBox';
import { ArrowRight, Box, Shield, Zap, Award, TrendingUp, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { applyStyle } from '@/utils/styleHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultContent = {
  hero: {
    badge: 'PREMIUM PACKAGING SOLUTIONS',
    title: 'Excellence in',
    titleHighlight: 'Packaging Innovation',
    description: 'Challenge Packaging Industries delivers premium corrugated carton boxes with sustainable materials and precision engineering for businesses worldwide.',
  },
  stats: {
    items: [
      { icon: 'Box', value: '250K+', label: 'Boxes Produced', sublabel: 'annually' },
      { icon: 'Users', value: '500+', label: 'Happy Clients', sublabel: 'worldwide' },
      { icon: 'Award', value: '25+', label: 'Years Experience', sublabel: 'in industry' },
      { icon: 'TrendingUp', value: '98%', label: 'Satisfaction Rate', sublabel: 'guaranteed' },
    ],
  },
  whyChooseUs: {
    badge: 'WHY CHOOSE US',
    title: 'Excellence in Every Box',
    description: 'We combine cutting-edge technology with sustainable practices to deliver the finest packaging solutions for your business.',
  },
  features: {
    items: [
      { icon: 'Shield', title: 'Quality Assured', description: 'ISO certified manufacturing with rigorous quality control ensuring excellence in every product.' },
      { icon: 'Zap', title: 'Fast Delivery', description: 'Quick turnaround times with efficient logistics, delivering your orders when you need them.' },
      { icon: 'Box', title: 'Custom Solutions', description: 'Tailored packaging designs crafted to meet your specific business requirements perfectly.' },
    ],
  },
  cta: {
    title: 'Ready to Get Started?',
    description: 'Contact us today for a quote or to discuss your packaging needs with our expert team.',
    buttonText: 'Get a Quote',
  },
};

const iconMap = { Box, Shield, Zap, Award, TrendingUp, Users };

const heroFields = [
  { key: 'badge', label: 'Badge Text', type: 'text', styled: true },
  { key: 'title', label: 'Title Line 1', type: 'title', styled: true },
  { key: 'titleHighlight', label: 'Title Highlight (green)', type: 'title', styled: true },
  { key: 'description', label: 'Description', type: 'text', multiline: true, styled: true },
];

const statsFields = [
  { key: 'items', label: 'Stats', type: 'list', itemFields: [
    { key: 'value', label: 'Value', type: 'text' },
    { key: 'label', label: 'Label', type: 'text' },
    { key: 'sublabel', label: 'Sub-label', type: 'text' },
    { key: 'icon', label: 'Icon (Box/Users/Award/TrendingUp)', type: 'text' },
  ]},
];

const whyFields = [
  { key: 'badge', label: 'Badge', type: 'text', styled: true },
  { key: 'title', label: 'Title', type: 'title', styled: true },
  { key: 'description', label: 'Description', type: 'text', multiline: true, styled: true },
];

const featuresFields = [
  { key: 'items', label: 'Features', type: 'list', itemFields: [
    { key: 'icon', label: 'Icon (Shield/Zap/Box)', type: 'text' },
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
  ]},
];

const ctaFields = [
  { key: 'title', label: 'Title', type: 'title', styled: true },
  { key: 'description', label: 'Description', type: 'text', multiline: true, styled: true },
  { key: 'buttonText', label: 'Button Text', type: 'text' },
];

export default function HomePage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const [content, setContent] = useState(defaultContent);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pages/home`);
        if (r.data?.content) {
          const c = r.data.content;
          // Migrate old flat arrays to new shape
          if (Array.isArray(c.stats)) c.stats = { items: c.stats };
          if (Array.isArray(c.features)) c.features = { items: c.features };
          setContent({ ...defaultContent, ...c });
        }
      } catch (error) { console.error('Failed to load home content:', error); }
    })();
  }, []);

  const updateSection = (key) => (data) => setContent(prev => ({ ...prev, [key]: data }));

  const stats = content.stats?.items || content.stats || [];
  const features = content.features?.items || content.features || [];
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden group" data-testid="hero-section">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(2,20,10,0.9) 40%, rgba(5,30,15,0.85) 100%)' }}></div>
        <div className="absolute inset-0 hidden md:block pointer-events-none"><RobotHandBox /></div>
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full opacity-10 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(var(--primary)), transparent)', filter: 'blur(120px)' }}></div>

        <SectionEditor user={user} sectionTitle="Hero Section" fields={heroFields} content={content.hero} pageName="home" sectionKey="hero" onSave={updateSection('hero')} />

        <motion.div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" style={{ opacity, scale }}>
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mb-8">
              <span className="badge" style={applyStyle(content.hero?.badgeStyle)}>{content.hero.badge}</span>
            </motion.div>
            <motion.h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mb-8 leading-[1.05] tracking-tight"
              initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", ...applyStyle(content.hero?.titleStyle) }}>
              {content.hero.title}
              <span className="block mt-2" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-light)), hsl(var(--primary)))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', ...applyStyle(content.hero?.titleHighlightStyle) }}>
                {content.hero.titleHighlight}
              </span>
            </motion.h1>
            <motion.p className="text-lg sm:text-xl text-gray-300/90 mb-12 leading-relaxed max-w-2xl" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
              style={applyStyle(content.hero?.descriptionStyle)}>
              {content.hero.description}
            </motion.p>
            <motion.div className="flex flex-wrap gap-5" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }}>
              <Link to="/products"><motion.button className="modern-button" data-testid="explore-products-button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>Explore Products <ArrowRight className="inline-block ml-2 w-5 h-5" /></motion.button></Link>
              <Link to="/contact"><motion.button className="modern-button-secondary text-white border-white/20 hover:bg-white/10 hover:text-primary hover:border-primary" data-testid="contact-us-button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>Contact Us</motion.button></Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-28 gradient-mesh relative noise-overlay section-fade-top section-fade-bottom group">
        <SectionEditor user={user} sectionTitle="Stats Section" fields={statsFields} content={content.stats} pageName="home" sectionKey="stats" onSave={updateSection('stats')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(Array.isArray(stats) ? stats : []).map((stat, index) => {
              const Icon = iconMap[stat.icon] || Box;
              return (
                <motion.div key={stat.label || `stat-${index}`} className="stat-card text-center group/card" data-testid={`stat-${index}`} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: index * 0.12 }} viewport={{ once: true }}>
                  <motion.div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} whileHover={{ rotate: 360, scale: 1.15 }} transition={{ duration: 0.7 }}>
                    <Icon className="w-8 h-8 text-primary-foreground" />
                  </motion.div>
                  <p className="text-4xl lg:text-5xl font-black text-foreground mb-2 font-mono-spec tracking-tighter">{stat.value}</p>
                  <p className="text-sm font-semibold text-foreground/80">{stat.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.sublabel}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-28 relative overflow-hidden group">
        <div className="gradient-radial-glow absolute inset-0"></div>
        <div className="subtle-grid absolute inset-0 opacity-40"></div>
        <SectionEditor user={user} sectionTitle="Why Choose Us" fields={whyFields} content={content.whyChooseUs} pageName="home" sectionKey="whyChooseUs" onSave={updateSection('whyChooseUs')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div className="text-center mb-20" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <span className="badge mb-6" style={applyStyle(content.whyChooseUs?.badgeStyle)}>{content.whyChooseUs.badge}</span>
            <h2 className="text-4xl lg:text-6xl font-black text-foreground mb-6 tracking-tighter" style={applyStyle(content.whyChooseUs?.titleStyle)}>{content.whyChooseUs.title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" style={applyStyle(content.whyChooseUs?.descriptionStyle)}>{content.whyChooseUs.description}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(Array.isArray(features) ? features : []).map((feature, index) => {
              const Icon = iconMap[feature.icon] || Box;
              return (
                <motion.div key={feature.title || `feature-${index}`} className="feature-card group/card" data-testid={`feature-${index}`} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: index * 0.2 }} viewport={{ once: true }}>
                  <motion.div className="icon-container mb-8" whileHover={{ rotate: 5, scale: 1.15 }}><Icon className="w-8 h-8 text-primary-foreground" /></motion.div>
                  <h3 className="text-2xl font-black text-foreground mb-4 tracking-tight">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  <div className="mt-6 h-1 w-12 rounded-full" style={{ background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary-end)))' }}></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section id="contact-cta" className="py-28 relative overflow-hidden group" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }} viewport={{ once: true }}>
        <div className="absolute inset-0" style={{ background: 'hsl(var(--primary))' }}></div>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }}></div>
        <SectionEditor user={user} sectionTitle="Call to Action" fields={ctaFields} content={content.cta} pageName="home" sectionKey="cta" onSave={updateSection('cta')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h2 className="text-4xl lg:text-6xl font-black text-primary-foreground mb-8 tracking-tighter" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={applyStyle(content.cta?.titleStyle)}>{content.cta.title}</motion.h2>
          <motion.p className="text-lg text-primary-foreground/70 mb-12 max-w-2xl mx-auto" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }}
            style={applyStyle(content.cta?.descriptionStyle)}>{content.cta.description}</motion.p>
          <Link to="/contact">
            <motion.button className="px-14 py-5 text-lg font-black bg-primary-foreground text-primary rounded-xl hover:bg-primary-foreground/90 transition-all shadow-2xl" data-testid="get-quote-button" whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} whileTap={{ scale: 0.95 }}>
              {content.cta.buttonText} <ArrowRight className="inline-block ml-2 w-5 h-5" />
            </motion.button>
          </Link>
        </div>
      </motion.section>

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
